import {
  isAnyOf,
  appActive,
  startListening,
  socketConnected,
} from '@betty/toolkit/state';
import { QuestUpdate } from './quests.types';
import {
  fetchQuest,
  fetchQuests,
  removeQuest,
  selectQuest,
  selectQuests,
  updateQuest,
} from './quests.slice';
import { getNextExpiringQuest, getNextUpcomingQuest } from './quests.utils';
import { convertHrsToMs, MINUTE_IN_MILLISECONDS } from '@betty/toolkit/utils';
import { logInfo } from '@betty/toolkit/logging';
import { Quest, QuestStatus } from '../services/QuestsService.schemas';

let nextQuestTimer: NodeJS.Timeout;
let nextExpiringQuestTimer: NodeJS.Timeout;

startListening({
  matcher: isAnyOf(fetchQuests.fulfilled, fetchQuest.settled),
  effect: (_, { getState, dispatch }) => {
    logInfo('Continuously fetch the next upcoming quest by id');

    if (nextQuestTimer) {
      clearTimeout(nextQuestTimer);
    }

    const quests = selectQuests(getState());
    const nextUpcomingQuest = getNextUpcomingQuest(quests);

    if (nextUpcomingQuest) {
      const fetchDelay =
        new Date(nextUpcomingQuest.startDate).getTime() - Date.now();

      // Fetching quests that are too far ahead in time overflows the 32-bit Integer of the setTimeout delay and causes recursive network calls
      // Only fetch the next quest if it's starting within 24 hours
      if (fetchDelay > 0 && fetchDelay <= convertHrsToMs(24)) {
        nextQuestTimer = setTimeout(() => {
          dispatch(fetchQuest(nextUpcomingQuest?.promotionId));
        }, fetchDelay);
      }
    }
  },
});

startListening({
  matcher: isAnyOf(fetchQuests.fulfilled, fetchQuest.fulfilled, appActive),
  effect: (_, { getState, dispatch }) => {
    const waitForQuestExpiring = () => {
      clearTimeout(nextExpiringQuestTimer);

      const nextExpiringQuest = getNextExpiringQuest(selectQuests(getState()));

      if (nextExpiringQuest) {
        const delay = MINUTE_IN_MILLISECONDS;
        const timeDiff =
          new Date(nextExpiringQuest.endDate).getTime() - Date.now();

        nextExpiringQuestTimer = setTimeout(
          () => {
            if (new Date(nextExpiringQuest.endDate).getTime() <= Date.now()) {
              dispatch(removeQuest(nextExpiringQuest.promotionId));
            }

            waitForQuestExpiring();
          },
          timeDiff < delay ? timeDiff : delay,
        );
      }
    };

    waitForQuestExpiring();
  },
});

startListening({
  actionCreator: socketConnected,
  effect: (action, { getState, dispatch }) => {
    const socketConnection = action.payload;

    socketConnection.on(
      'InPeriodPromotionProgressUpdated',
      (questUpdate: QuestUpdate) => {
        const state = getState();
        const quest = selectQuest(state, questUpdate.promotionId);

        logInfo('WS', 'InPeriodPromotionProgressUpdated', questUpdate);

        if (!quest || !quest.isOptedIn) {
          return;
        }

        const updatedQuest: Quest = {
          ...quest,
          status:
            questUpdate.playerPromotionStatus === 'Qualified'
              ? QuestStatus.Completed
              : quest.status,
          subTypeMeta: {
            ...quest.subTypeMeta,
            currentAccumulation: questUpdate.currentAccumulation,
            targetAccumulation: questUpdate.targetAccumulation,
          },
        };

        dispatch(updateQuest(updatedQuest));
      },
    );
  },
});
